import React, { useState, useEffect } from 'react';
import { siteConfigApi } from '../utils/api';
import { useLanguageStore, t } from '../stores/languageStore';

const PrivacyPolicy: React.FC = () => {
  const { language } = useLanguageStore();
  const [contactInfo, setContactInfo] = useState({
    email: 'support@taohuatan.com',
    telegram: '@TaoHuaTan'
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      setIsLoading(true);
      try {
        const response = await siteConfigApi.getPublicConfigs();
        if (response.success && response.data) {
          const contactConfig = response.data['about_us_contact'];
          if (contactConfig) {
            const contactLines = contactConfig.split('\n');
            const newContactInfo = { ...contactInfo };
            contactLines.forEach(line => {
              if (line.includes('电子邮件')) {
                newContactInfo.email = line.replace('电子邮件：', '').trim();
              } else if (line.includes('Telegram')) {
                newContactInfo.telegram = line.replace('Telegram：', '').trim();
              }
            });
            setContactInfo(newContactInfo);
          }
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {language === 'en' ? 'Peach Blossom Forum Privacy Policy' : '桃花坛隐私政策'}
          </h1>

          <div className="prose prose-gray max-w-none">
            <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 font-medium mb-2">⚠️ {t('privacyPolicy.fraudAlert')}</p>
              <p className="text-red-700 text-sm mb-3">
                {t('privacyPolicy.fraudAlertDesc')}
              </p>
              <ul className="text-red-700 text-sm list-disc list-inside mb-3 space-y-1">
                <li>{t('privacyPolicy.scamType1')}</li>
                <li>{t('privacyPolicy.scamType2')}</li>
                <li>{t('privacyPolicy.scamType3')}</li>
                <li>{t('privacyPolicy.scamType4')}</li>
                <li>{t('privacyPolicy.scamType5')}</li>
              </ul>
              <p className="text-red-700 text-sm font-medium mb-1">{t('privacyPolicy.safetyTip')}</p>
              <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
                <li>{t('privacyPolicy.safetyTip1')}</li>
                <li>{t('privacyPolicy.safetyTip2')}</li>
                <li>{t('privacyPolicy.safetyTip3')}</li>
                <li>{t('privacyPolicy.safetyTip4')}</li>
                <li>{t('privacyPolicy.safetyTip5')}</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section1')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.intro1')}
            </p>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.intro2')}
            </p>
            <p className="text-gray-600">
              {t('privacyPolicy.intro3')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section2')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section2Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section2_1')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section2_1Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.username')}</strong>：{t('privacyPolicy.usernameDesc')}</li>
              <li><strong>{t('privacyPolicy.email')}</strong>：{t('privacyPolicy.emailDesc')}</li>
              <li><strong>{t('privacyPolicy.password')}</strong>：{t('privacyPolicy.passwordDesc')}</li>
              <li><strong>{t('privacyPolicy.avatar')}</strong>：{t('privacyPolicy.avatarDesc')}</li>
              <li><strong>{t('privacyPolicy.bio')}</strong>：{t('privacyPolicy.bioDesc')}</li>
              <li><strong>{t('privacyPolicy.telegram')}</strong>：{t('privacyPolicy.telegramDesc')}</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section2_2')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section2_2Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.ip')}</strong>：{t('privacyPolicy.ipDesc')}</li>
              <li><strong>{t('privacyPolicy.browser')}</strong>：{t('privacyPolicy.browserDesc')}</li>
              <li><strong>{t('privacyPolicy.device')}</strong>：{t('privacyPolicy.deviceDesc')}</li>
              <li><strong>{t('privacyPolicy.accessLog')}</strong>：{t('privacyPolicy.accessLogDesc')}</li>
              <li><strong>{t('privacyPolicy.interactionLog')}</strong>：{t('privacyPolicy.interactionLogDesc')}</li>
              <li><strong>{t('privacyPolicy.searchLog')}</strong>：{t('privacyPolicy.searchLogDesc')}</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section2_3')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section2_3Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.amount')}</strong>：{t('privacyPolicy.amountDesc')}</li>
              <li><strong>{t('privacyPolicy.paymentMethod')}</strong>：{t('privacyPolicy.paymentMethodDesc')}</li>
              <li><strong>{t('privacyPolicy.transactionTime')}</strong>：{t('privacyPolicy.transactionTimeDesc')}</li>
              <li><strong>{t('privacyPolicy.transactionStatus')}</strong>：{t('privacyPolicy.transactionStatusDesc')}</li>
              <li><strong>{t('privacyPolicy.walletAddress')}</strong>：{t('privacyPolicy.walletAddressDesc')}</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section2_4')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section2_4Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.sessionCookie')}</strong>：{t('privacyPolicy.sessionCookieDesc')}</li>
              <li><strong>{t('privacyPolicy.preferenceCookie')}</strong>：{t('privacyPolicy.preferenceCookieDesc')}</li>
              <li><strong>{t('privacyPolicy.analyticsCookie')}</strong>：{t('privacyPolicy.analyticsCookieDesc')}</li>
              <li><strong>{t('privacyPolicy.deviceId')}</strong>：{t('privacyPolicy.deviceIdDesc')}</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section2_5')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section2_5Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.paymentProvider')}</strong>：{t('privacyPolicy.paymentProviderDesc')}</li>
              <li><strong>{t('privacyPolicy.analyticsProvider')}</strong>：{t('privacyPolicy.analyticsProviderDesc')}</li>
              <li><strong>{t('privacyPolicy.securityProvider')}</strong>：{t('privacyPolicy.securityProviderDesc')}</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section3')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section3Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section3_1')}
            </h3>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              {t('privacyPolicy.section3_1Items').split('；').map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section3_2')}
            </h3>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              {t('privacyPolicy.section3_2Items').split('；').map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section3_3')}
            </h3>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              {t('privacyPolicy.section3_3Items').split('；').map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section3_4')}
            </h3>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              {t('privacyPolicy.section3_4Items').split('；').map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section3_5')}
            </h3>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              {t('privacyPolicy.section3_5Items').split('；').map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section4')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section4Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section4_1')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section4_1Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.paymentProcessor')}</strong>：{t('privacyPolicy.paymentProcessorDesc')}</li>
              <li><strong>{t('privacyPolicy.cloudProvider')}</strong>：{t('privacyPolicy.cloudProviderDesc')}</li>
              <li><strong>{t('privacyPolicy.securityService')}</strong>：{t('privacyPolicy.securityServiceDesc')}</li>
              <li><strong>{t('privacyPolicy.analyticsService')}</strong>：{t('privacyPolicy.analyticsServiceDesc')}</li>
            </ul>
            <p className="text-gray-600">
              {t('privacyPolicy.section4_1Note')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section4_2')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section4_2Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section4_3')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section4_3Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section4_4')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section4_4Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section4_5')}
            </h3>
            <p className="text-gray-600">
              {t('privacyPolicy.section4_5Desc')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section5')}
            </h2>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section5_1')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section5_1Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.encryptedStorage')}</strong>：{t('privacyPolicy.encryptedStorageDesc')}</li>
              <li><strong>{t('privacyPolicy.accessControl')}</strong>：{t('privacyPolicy.accessControlDesc')}</li>
              <li><strong>{t('privacyPolicy.regularBackup')}</strong>：{t('privacyPolicy.regularBackupDesc')}</li>
              <li><strong>{t('privacyPolicy.securityAudit')}</strong>：{t('privacyPolicy.securityAuditDesc')}</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section5_2')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section5_2Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section5_3')}
            </h3>
            <p className="text-gray-600">
              {t('privacyPolicy.section5_3Desc')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section6')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section6Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_1')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_1Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_2')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_2Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_3')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_3Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_4')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_4Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_5')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_5Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_6')}
            </h3>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section6_6Desc')}
            </p>

            <h3 className="text-lg font-medium text-gray-700 mt-4 mb-2">
              {t('privacyPolicy.section6_7')}
            </h3>
            <p className="text-gray-600">
              {t('privacyPolicy.section6_7Desc')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section7')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section7Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.accountInfo')}</strong>：{t('privacyPolicy.accountInfoDesc')}</li>
              <li><strong>{t('privacyPolicy.transactionInfo')}</strong>：{t('privacyPolicy.transactionInfoDesc')}</li>
              <li><strong>{t('privacyPolicy.logData')}</strong>：{t('privacyPolicy.logDataDesc')}</li>
              <li><strong>{t('privacyPolicy.userContent')}</strong>：{t('privacyPolicy.userContentDesc')}</li>
            </ul>
            <p className="text-gray-600">
              {t('privacyPolicy.section7Note')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section8')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section8Desc')}
            </p>
            <p className="text-gray-600">
              {t('privacyPolicy.section8Note')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section9')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section9Desc')}
            </p>
            <p className="text-gray-600">
              {t('privacyPolicy.section9Note')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section10')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section10Desc')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section11')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section11Desc')}
            </p>
            <ul className="text-gray-600 mb-4 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.necessaryCookie')}</strong>：{t('privacyPolicy.necessaryCookieDesc')}</li>
              <li><strong>{t('privacyPolicy.analyticsCookies')}</strong>：{t('privacyPolicy.analyticsCookiesDesc')}</li>
              <li><strong>{t('privacyPolicy.preferenceCookies')}</strong>：{t('privacyPolicy.preferenceCookiesDesc')}</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section12')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('privacyPolicy.section12Desc')}
            </p>
            <p className="text-gray-600">
              {t('privacyPolicy.section12Note')}
            </p>

            <h2 className="text-xl font-semibold text-gray-800 mt-8 mb-4">
              {t('privacyPolicy.section13')}
            </h2>
            <p className="text-gray-600 mb-3">
              {t('privacyPolicy.section13Desc')}
            </p>
            <ul className="text-gray-600 list-disc list-inside space-y-2">
              <li><strong>{t('privacyPolicy.contactEmail')}</strong>：{isLoading ? t('privacyPolicy.loadingText') : contactInfo.email}</li>
              <li><strong>{t('privacyPolicy.contactTelegram')}</strong>：{isLoading ? t('privacyPolicy.loadingText') : contactInfo.telegram}</li>
            </ul>

            <div className="mt-12 p-6 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600 text-sm text-center">
                {language === 'en' ? 'Privacy Policy v1.0 · Last updated: May 2026' : '桃花坛隐私政策 v1.0 · 最后更新：2026年5月'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;